// src/components/auth/ReAuthModal.jsx
import React, { useState } from 'react';
import { FiLock, FiShield, FiEye, FiEyeOff } from 'react-icons/fi';
import { reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import auditService from '../../services/auditService';

const ReAuthModal = ({ isOpen, onClose, onSuccess, actionType = 'sensitive_action' }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { currentUser, userProfile } = useAuth();

  const handleReAuth = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    if (!currentUser?.email) {
      toast.error('No authenticated user found');
      return;
    }

    setLoading(true);
    try {
      // Create credential for re-authentication
      const credential = EmailAuthProvider.credential(currentUser.email, password);
      
      // Re-authenticate the user
      await reauthenticateWithCredential(currentUser, credential);
      
      // Log the re-authentication event
      await auditService.logReAuthentication(currentUser.uid, userProfile, actionType);
      
      // Store re-auth timestamp for future checks
      sessionStorage.setItem('lastReAuth', Date.now().toString());
      
      toast.success('Identity verified successfully');
      onSuccess?.();
      onClose();
      
    } catch (error) {
      console.error('Re-authentication failed:', error);
      
      // Log failed re-auth attempt
      await auditService.logAuthEvent('re_auth_failed', currentUser.uid, userProfile, {
        reason: actionType,
        error: error.code
      });
      
      switch (error.code) {
        case 'auth/wrong-password':
          toast.error('Incorrect password');
          break;
        case 'auth/too-many-requests':
          toast.error('Too many failed attempts. Please try again later');
          break;
        default:
          toast.error('Authentication failed. Please try again');
      }
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  const getActionDescription = () => {
    switch (actionType) {
      case 'data_export':
        return 'export client data';
      case 'patient_download':
        return 'download patient records';
      case 'admin_config_change':
        return 'modify system configuration';
      case 'sensitive_action':
      default:
        return 'perform this sensitive action';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
            <FiShield className="h-8 w-8 text-blue-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Verify Your Identity
          </h2>
          
          <p className="text-gray-600 text-sm">
            For security and compliance, please re-enter your password to {getActionDescription()}.
          </p>
        </div>

        <form onSubmit={handleReAuth} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <div className="relative">
              <FiLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your password"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <FiShield className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Data Protection Compliance</p>
                <p>This verification is required under GDPR Art. 32 and PDPA Sec. 24 to ensure authorized access to sensitive data.</p>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password}
              className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Verifying...' : 'Verify Identity'}
            </button>
          </div>
        </form>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Your password is never stored and this verification is logged for audit purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReAuthModal;
